// Left panel of the Delivery view: vertical feature list. Each card
// surfaces the per-feature scoring (effort / value / tags) read from the
// `featureScoring` map on the analysis blob, plus the MU containment and
// a hide toggle. Chips are click-editable via popovers, write back via
// PATCH mutations.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EyeOff, Eye, Plus, X, Pencil } from 'lucide-react';
import type {
  DiscoveryFeature,
  DeliveryAnalysisPayload,
  DeliveryMutation,
  EffortPoints,
  BusinessValue,
  FeatureScoring,
} from '../hooks/sessionTypes';
import { FIBONACCI_EFFORTS, VALUE_LEVELS, UNASSIGNED_MU } from '../hooks/sessionTypes';
import {
  muColor,
  muOfFeature,
  isHidden,
  scoringFor,
} from '../utils/deliveryAnalysisModel';
import '../styles/DeliveryFeatureList.css';

interface Props {
  features: DiscoveryFeature[];
  payload: DeliveryAnalysisPayload;
  selectedFeatureId: string | null;
  onSelect: (id: string | null) => void;
  onImportClick: () => void;
  onPatch: (m: DeliveryMutation) => Promise<void>;
}

type SortKey = 'mu' | 'value' | 'effort' | 'name';

const VALUE_RANK: Record<BusinessValue, number> = { high: 0, med: 1, low: 2 };

const DeliveryFeatureList: React.FC<Props> = ({
  features, payload, selectedFeatureId, onSelect, onImportClick, onPatch,
}) => {
  const [sort, setSort] = useState<SortKey>('mu');
  const [muFilter, setMuFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'extracted' | 'csv'>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'bc' | 'aggregate' | 'feature'>('all');
  const [showHidden, setShowHidden] = useState(false);

  const knownMus = useMemo(
    () => payload.migrationUnits.map((m) => m.name),
    [payload.migrationUnits],
  );

  const muByFeatureId = useMemo(() => {
    const m = new Map<string, string>();
    for (const mu of payload.migrationUnits) for (const id of mu.featureIds) m.set(id, mu.name);
    return m;
  }, [payload.migrationUnits]);

  const filtered = useMemo(() => {
    const out = features.filter((f) => {
      if (!showHidden && isHidden(payload, f.candidateId)) return false;
      if (sourceFilter !== 'all' && (f.source || 'extracted') !== sourceFilter) return false;
      if (scopeFilter !== 'all' && f.scopeType !== scopeFilter) return false;
      if (muFilter && (muByFeatureId.get(f.candidateId) || UNASSIGNED_MU) !== muFilter) return false;
      return true;
    });

    out.sort((x, y) => {
      const sx = scoringFor(payload, x.candidateId);
      const sy = scoringFor(payload, y.candidateId);
      if (sort === 'mu') {
        const mx = muByFeatureId.get(x.candidateId) || 'zzz';
        const my = muByFeatureId.get(y.candidateId) || 'zzz';
        if (mx !== my) return mx.localeCompare(my);
        return x.name.localeCompare(y.name);
      }
      if (sort === 'value') {
        const vx = sx.value ? VALUE_RANK[sx.value] : 99;
        const vy = sy.value ? VALUE_RANK[sy.value] : 99;
        if (vx !== vy) return vx - vy;
        return (sy.effort || 0) - (sx.effort || 0);
      }
      if (sort === 'effort') return (sy.effort || 0) - (sx.effort || 0);
      return x.name.localeCompare(y.name);
    });
    return out;
  }, [features, payload, sort, muFilter, sourceFilter, scopeFilter, showHidden, muByFeatureId]);

  // Only show the scope-type filter when at least one feature has a scope type.
  const hasScopeTypes = useMemo(() => features.some((f) => !!f.scopeType), [features]);

  const hiddenCount = features.filter((f) => isHidden(payload, f.candidateId)).length;

  return (
    <section className="dv-left">
      <header className="dv-left-header">
        <div className="dv-left-title-row">
          <h2 className="dv-left-title">Features</h2>
          <span className="dv-left-count">{filtered.length} of {features.length}</span>
          <button
            type="button"
            className="dv-btn dv-btn--primary dv-left-import"
            onClick={onImportClick}
            title="Import features from CSV"
          >
            <Plus size={13} /> Import CSV
          </button>
        </div>
        <div className="dv-left-toolbar">
          <label className="dv-toolbar-label">
            Sort
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="mu">MU</option>
              <option value="value">Business value</option>
              <option value="effort">Effort (high → low)</option>
              <option value="name">Name</option>
            </select>
          </label>
          <label className="dv-toolbar-label">
            Source
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as 'all' | 'extracted' | 'csv')}>
              <option value="all">All</option>
              <option value="extracted">From flow</option>
              <option value="csv">Imported</option>
            </select>
          </label>
          {hasScopeTypes && (
            <label className="dv-toolbar-label">
              Type
              <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value as 'all' | 'bc' | 'aggregate' | 'feature')}>
                <option value="all">All</option>
                <option value="feature">Features</option>
                <option value="aggregate">Aggregates</option>
                <option value="bc">Bounded contexts</option>
              </select>
            </label>
          )}
          {knownMus.length > 0 && (
            <label className="dv-toolbar-label">
              MU
              <select value={muFilter || ''} onChange={(e) => setMuFilter(e.target.value || null)}>
                <option value="">All</option>
                {knownMus.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
          )}
          {hiddenCount > 0 && (
            <button
              type="button"
              className={`dv-toolbar-toggle ${showHidden ? 'dv-toolbar-toggle--active' : ''}`}
              onClick={() => setShowHidden((v) => !v)}
            >
              {showHidden ? <Eye size={12} /> : <EyeOff size={12} />}
              {hiddenCount} hidden
            </button>
          )}
        </div>
      </header>
      <div className="dv-left-body">
        {filtered.length === 0 ? (
          <div className="dv-left-empty">No features match the current filters.</div>
        ) : (
          <ul className="dv-card-list">
            {filtered.map((f) => (
              <FeatureCard
                key={f.candidateId}
                feature={f}
                payload={payload}
                selected={selectedFeatureId === f.candidateId}
                onSelect={() => onSelect(selectedFeatureId === f.candidateId ? null : f.candidateId)}
                onPatch={onPatch}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default DeliveryFeatureList;

// ── Card ──────────────────────────────────────────────────────────

interface CardProps {
  feature: DiscoveryFeature;
  payload: DeliveryAnalysisPayload;
  selected: boolean;
  onSelect: () => void;
  onPatch: (m: DeliveryMutation) => Promise<void>;
}

type PopoverKind = 'effort' | 'value' | 'tags' | null;

const FeatureCard: React.FC<CardProps> = ({ feature, payload, selected, onSelect, onPatch }) => {
  const mu = muOfFeature(payload, feature.candidateId);
  const muName = mu?.name || UNASSIGNED_MU;
  const color = muColor(muName);
  const hidden = isHidden(payload, feature.candidateId);
  const isCsv = (feature.source || 'extracted') === 'csv';
  const scoring = scoringFor(payload, feature.candidateId);
  const [popover, setPopover] = useState<PopoverKind>(null);

  const toggleHidden = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPatch({ type: 'toggleHidden', featureId: feature.candidateId, hidden: !hidden });
  };

  return (
    <li
      className={`dv-card ${selected ? 'dv-card--selected' : ''} ${hidden ? 'dv-card--hidden' : ''}`}
      style={{ borderLeftColor: color }}
      onClick={onSelect}
    >
      <div className="dv-card-head">
        {feature.scopeType && (
          <span className={`dv-scope-pill dv-scope-pill--${feature.scopeType}`} title={`Scope type: ${feature.scopeType}`}>
            {feature.scopeType === 'bc' ? 'BC' : feature.scopeType === 'aggregate' ? 'Agg' : 'Feat'}
          </span>
        )}
        <h3 className="dv-card-name">{feature.name}</h3>
        <div className="dv-card-head-actions">
          {isCsv && <span className="dv-source-pill" title="Imported from CSV">CSV</span>}
          <button
            type="button"
            className="dv-card-icon-btn"
            onClick={toggleHidden}
            title={hidden ? 'Unhide feature' : 'Hide from grouping & stats'}
            aria-label={hidden ? 'Unhide feature' : 'Hide feature'}
          >
            {hidden ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
        </div>
      </div>
      {feature.description && <p className="dv-card-desc">{feature.description}</p>}

      <div className="dv-card-chips">
        <span className="dv-chip dv-chip--mu" style={{ borderColor: color, color }}>
          📦 {muName}
        </span>
        <ChipBtn
          className="dv-chip dv-chip--effort"
          style={effortChipStyle(scoring.effort)}
          onClick={(e) => { e.stopPropagation(); setPopover((p) => p === 'effort' ? null : 'effort'); }}
          title="Effort (Fibonacci story points). Click to edit."
        >
          ⚡ {scoring.effort != null ? `${scoring.effort} pts` : 'set effort'}
        </ChipBtn>
        <ChipBtn
          className={`dv-chip dv-chip--value ${scoring.value ? `dv-chip--value-${scoring.value}` : 'dv-chip--value-unset'}`}
          onClick={(e) => { e.stopPropagation(); setPopover((p) => p === 'value' ? null : 'value'); }}
          title="Business value. Click to edit."
        >
          💎 {scoring.value ? capitalize(scoring.value) : 'set value'}
        </ChipBtn>
        <ChipBtn
          className="dv-chip dv-chip--tags-edit"
          onClick={(e) => { e.stopPropagation(); setPopover((p) => p === 'tags' ? null : 'tags'); }}
          title="Edit tags"
        >
          <Pencil size={10} />
          {scoring.tags && scoring.tags.length > 0 ? scoring.tags.join(' ') : 'add tags'}
        </ChipBtn>
      </div>

      <div className="dv-card-foot">
        {feature.boundedContext && <span className="dv-meta-chip">◆ {feature.boundedContext}</span>}
        {feature.stream && <span className="dv-meta-chip dv-meta-chip--stream">{feature.stream}</span>}
        {feature.specConfidence && feature.specConfidence !== 'n/a' && (
          <span className={`dv-meta-chip dv-meta-chip--conf-${feature.specConfidence}`} title="Spec confidence">
            spec {feature.specConfidence}
          </span>
        )}
        {feature.priority && (
          <span className={`dv-meta-chip dv-meta-chip--prio-${feature.priority}`}>{feature.priority.toUpperCase()}</span>
        )}
        {feature.touchpoints?.map((tp) => (
          <span key={tp} className="dv-meta-chip">🖥 {tp}</span>
        ))}
      </div>

      {popover && (
        <ScoringPopover
          kind={popover}
          scoring={scoring}
          onPick={async (mutation) => {
            await onPatch(mutation);
            setPopover(null);
          }}
          onClear={async () => {
            await onPatch({ type: 'clearFeatureScoring', featureId: feature.candidateId, field: popover });
            setPopover(null);
          }}
          onChangeTags={async (tags) => {
            await onPatch({ type: 'setFeatureScoring', featureId: feature.candidateId, field: 'tags', value: tags });
          }}
          onClose={() => setPopover(null)}
          featureId={feature.candidateId}
        />
      )}
    </li>
  );
};

// ── Popover ──────────────────────────────────────────────────────

interface ScoringPopoverProps {
  kind: 'effort' | 'value' | 'tags';
  scoring: FeatureScoring;
  featureId: string;
  onPick: (mutation: DeliveryMutation) => Promise<void>;
  onClear: () => Promise<void>;
  onChangeTags: (tags: string[]) => Promise<void>;
  onClose: () => void;
}

const ScoringPopover: React.FC<ScoringPopoverProps> = ({
  kind, scoring, featureId, onPick, onClear, onChangeTags, onClose,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose]);

  return (
    <div ref={rootRef} className="dv-popover" onClick={(e) => e.stopPropagation()}>
      {kind === 'effort' && (
        <PopoverList
          header="Effort (Fibonacci)"
          current={scoring.effort}
          options={FIBONACCI_EFFORTS.map((v) => ({ value: v, label: `${v} pts` }))}
          onPick={(v) => onPick({ type: 'setFeatureScoring', featureId, field: 'effort', value: v as EffortPoints })}
          onClear={scoring.effort != null ? onClear : undefined}
          onClose={onClose}
        />
      )}
      {kind === 'value' && (
        <PopoverList
          header="Business value"
          current={scoring.value}
          options={VALUE_LEVELS.map((v) => ({ value: v, label: capitalize(v) }))}
          onPick={(v) => onPick({ type: 'setFeatureScoring', featureId, field: 'value', value: v as BusinessValue })}
          onClear={scoring.value ? onClear : undefined}
          onClose={onClose}
        />
      )}
      {kind === 'tags' && (
        <TagsEditor
          current={scoring.tags || []}
          onChange={onChangeTags}
          onClear={(scoring.tags && scoring.tags.length > 0) ? onClear : undefined}
          onClose={onClose}
        />
      )}
    </div>
  );
};

interface PopoverListProps<V> {
  header: string;
  current: V | undefined;
  options: { value: V; label: string }[];
  onPick: (v: V) => Promise<void>;
  onClear?: () => Promise<void>;
  onClose: () => void;
}

function PopoverList<V extends string | number>({
  header, current, options, onPick, onClear, onClose,
}: PopoverListProps<V>) {
  return (
    <div className="dv-popover-inner">
      <div className="dv-popover-head">
        <span>{header}</span>
        <button type="button" onClick={onClose} aria-label="Close"><X size={11} /></button>
      </div>
      <ul className="dv-popover-list">
        {options.map((o) => (
          <li key={String(o.value)}>
            <button
              type="button"
              className={`dv-popover-item ${o.value === current ? 'dv-popover-item--current' : ''}`}
              onClick={() => onPick(o.value)}
            >
              {o.label}
            </button>
          </li>
        ))}
      </ul>
      {onClear && (
        <button type="button" className="dv-popover-clear" onClick={onClear}>
          Clear value
        </button>
      )}
    </div>
  );
}

interface TagsEditorProps {
  current: string[];
  onChange: (tags: string[]) => Promise<void>;
  onClear?: () => Promise<void>;
  onClose: () => void;
}

const TagsEditor: React.FC<TagsEditorProps> = ({ current, onChange, onClear, onClose }) => {
  const [tags, setTags] = useState<string[]>(current);
  const [input, setInput] = useState('');

  const addTag = () => {
    const raw = input.trim().replace(/^#/, '').replace(/\s+/g, '-').toLowerCase();
    if (!raw) return;
    const candidate = `#${raw}`;
    if (!tags.includes(candidate) && tags.length < 3) {
      const next = [...tags, candidate];
      setTags(next);
      onChange(next);
    }
    setInput('');
  };

  const removeTag = (t: string) => {
    const next = tags.filter((x) => x !== t);
    setTags(next);
    onChange(next);
  };

  return (
    <div className="dv-popover-inner">
      <div className="dv-popover-head">
        <span>Tags (up to 3)</span>
        <button type="button" onClick={onClose} aria-label="Close"><X size={11} /></button>
      </div>
      <div className="dv-popover-tags">
        {tags.length === 0 && <span className="dv-popover-tags-empty">No tags yet.</span>}
        {tags.map((t) => (
          <span key={t} className="dv-popover-tag">
            {t}
            <button type="button" onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
              <X size={9} />
            </button>
          </span>
        ))}
      </div>
      <form
        className="dv-popover-new"
        onSubmit={(e) => { e.preventDefault(); addTag(); }}
      >
        <input
          type="text"
          placeholder="#api, #ux-heavy…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={20}
          disabled={tags.length >= 3}
        />
        <button type="submit" disabled={!input.trim() || tags.length >= 3}>Add</button>
      </form>
      {onClear && (
        <button type="button" className="dv-popover-clear" onClick={onClear}>
          Clear all tags
        </button>
      )}
    </div>
  );
};

// ── Small helpers ─────────────────────────────────────────────────

interface ChipBtnProps {
  className: string;
  style?: React.CSSProperties;
  onClick: (e: React.MouseEvent) => void;
  title: string;
  children: React.ReactNode;
}

const ChipBtn: React.FC<ChipBtnProps> = ({ className, style, onClick, title, children }) => (
  <button type="button" className={className} style={style} onClick={onClick} title={title}>
    {children}
  </button>
);

function effortChipStyle(effort: number | undefined): React.CSSProperties {
  if (effort == null) return { borderColor: '#cbd5e1', color: '#64748b' };
  let color = '#16a34a';
  if (effort > 2) color = '#ca8a04';
  if (effort > 5) color = '#ea580c';
  if (effort > 13) color = '#dc2626';
  return { borderColor: color, color };
}

function capitalize(s: string): string { return s.slice(0, 1).toUpperCase() + s.slice(1); }
